import { useCallback, useMemo } from 'react';

import {
  FolderType,
  SuborganizationDTOStatus,
  useActivateSuborganization,
  useDeactivateSuborganization,
  useHoldSuborganization,
  useResumeSuborganization
} from '@/api/generated';

import { useSuborganizationPermissions } from './permissions/useSuborganizationPermissions';

enum SuborganizationStatusActions {
  ACTIVATE = 'ACTIVATE',
  DEACTIVATE = 'DEACTIVATE',
  RESUME = 'RESUME',
  HOLD = 'HOLD'
}

interface SuborganizationStatusTransition {
  from: SuborganizationDTOStatus;
  to: SuborganizationDTOStatus;
  name: SuborganizationStatusActions;
  handler: () => Promise<void>;
}

export const useSuborganizationStatusActions = (options: {
  organizationSlug: string;
  suborganizationSlug: string;
  currentStatus?: SuborganizationDTOStatus;
  onChange: (newStatus: SuborganizationDTOStatus) => void;
}) => {
  const { organizationSlug, suborganizationSlug, currentStatus, onChange } = options;

  const { mutateAsync: activateSuborganization, isLoading: isActivateSuborgLoading } = useActivateSuborganization();
  const { mutateAsync: deactivateSuborganization, isLoading: isDeactivateSuborgLoading } =
    useDeactivateSuborganization();
  const { mutateAsync: resumeSuborganization, isLoading: isResumeSuborgLoading } = useResumeSuborganization();
  const { mutateAsync: holdSuborganization, isLoading: isHoldSuborgLoading } = useHoldSuborganization();

  const { value: suborganizationPermissions } = useSuborganizationPermissions({
    variables: { organizationSlug, locationSlug: suborganizationSlug, locationType: FolderType.SUB_ORGANIZATION }
  });

  const isStatusLoading =
    isActivateSuborgLoading || isDeactivateSuborgLoading || isResumeSuborgLoading || isHoldSuborgLoading;

  const statusTransitions = useMemo(
    () =>
      [
        (!suborganizationPermissions || suborganizationPermissions.canActivate) && {
          from: SuborganizationDTOStatus.DRAFT,
          to: SuborganizationDTOStatus.ACTIVE,
          name: SuborganizationStatusActions.ACTIVATE,
          handler: () => activateSuborganization({ slug: organizationSlug, subSlug: suborganizationSlug })
        },
        (!suborganizationPermissions || suborganizationPermissions.canActivate) && {
          from: SuborganizationDTOStatus.INACTIVE,
          to: SuborganizationDTOStatus.ACTIVE,
          name: SuborganizationStatusActions.ACTIVATE,
          handler: () => activateSuborganization({ slug: organizationSlug, subSlug: suborganizationSlug })
        },
        (!suborganizationPermissions || suborganizationPermissions.canDeactivate) && {
          from: SuborganizationDTOStatus.ACTIVE,
          to: SuborganizationDTOStatus.INACTIVE,
          name: SuborganizationStatusActions.DEACTIVATE,
          handler: () => deactivateSuborganization({ organizationSlug, suborganizationSlug })
        },
        (!suborganizationPermissions || suborganizationPermissions.canHold) && {
          from: SuborganizationDTOStatus.ACTIVE,
          to: SuborganizationDTOStatus.ON_HOLD,
          name: SuborganizationStatusActions.HOLD,
          handler: () => holdSuborganization({ organizationSlug, suborganizationSlug })
        },
        (!suborganizationPermissions || suborganizationPermissions.canActivate) && {
          from: SuborganizationDTOStatus.ON_HOLD,
          to: SuborganizationDTOStatus.ACTIVE,
          name: SuborganizationStatusActions.RESUME,
          handler: () => resumeSuborganization({ organizationSlug, suborganizationSlug })
        }
      ].filter(Boolean) as SuborganizationStatusTransition[],
    [
      suborganizationPermissions,
      activateSuborganization,
      organizationSlug,
      suborganizationSlug,
      deactivateSuborganization,
      holdSuborganization,
      resumeSuborganization
    ]
  );

  const availableTransitions = useMemo(
    () => statusTransitions.filter(({ from }) => from === currentStatus),
    [currentStatus, statusTransitions]
  );
  const availableStatuses = useMemo(
    () => [currentStatus, ...availableTransitions.map(({ to }) => to)],
    [availableTransitions, currentStatus]
  );
  const isFinalState = useMemo(() => !availableTransitions.length, [availableTransitions]);
  const availableActions = useMemo(() => availableTransitions.map(({ name }) => name), [availableTransitions]);

  const isActivateAvailable = useMemo(
    () => availableActions.includes(SuborganizationStatusActions.ACTIVATE),
    [availableActions]
  );
  const isDeactivateAvailable = useMemo(
    () => availableActions.includes(SuborganizationStatusActions.DEACTIVATE),
    [availableActions]
  );
  const isHoldAvailable = useMemo(
    () => availableActions.includes(SuborganizationStatusActions.HOLD),
    [availableActions]
  );
  const isResumeAvailable = useMemo(
    () => availableActions.includes(SuborganizationStatusActions.RESUME),
    [availableActions]
  );

  const handleStatusChange = useCallback(
    async (newStatus: SuborganizationDTOStatus) => {
      const originalStatus = currentStatus;
      try {
        const transition = availableTransitions.find(({ from, to }) => originalStatus === from && newStatus === to);
        if (!transition) {
          throw new Error(`Transition impossible: ${originalStatus} => ${newStatus}`);
        }
        onChange(newStatus);
        if (transition.handler) {
          await transition.handler();
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Transition is not implemented: ${transition.name}`);
        }
      } catch (e) {
        // TODO: proper logging
        console.error(e); // eslint-disable-line no-console
        onChange(originalStatus!);
      }
    },
    [availableTransitions, currentStatus, onChange]
  );

  const handleActivate = useCallback(() => {
    if (isActivateAvailable) {
      handleStatusChange(SuborganizationDTOStatus.ACTIVE);
    }
  }, [handleStatusChange, isActivateAvailable]);

  const handleDeactivate = useCallback(() => {
    if (isDeactivateAvailable) {
      handleStatusChange(SuborganizationDTOStatus.INACTIVE);
    }
  }, [handleStatusChange, isDeactivateAvailable]);

  const handleHold = useCallback(() => {
    if (isHoldAvailable) {
      handleStatusChange(SuborganizationDTOStatus.ON_HOLD);
    }
  }, [handleStatusChange, isHoldAvailable]);

  const handleResume = useCallback(() => {
    if (isResumeAvailable) {
      handleStatusChange(SuborganizationDTOStatus.ACTIVE);
    }
  }, [handleStatusChange, isResumeAvailable]);

  return {
    isLoading: isStatusLoading,
    isFinalState,
    availableStatuses,
    activate: handleActivate,
    deactivate: handleDeactivate,
    hold: handleHold,
    resume: handleResume,
    change: handleStatusChange,
    isActivateAvailable,
    isDeactivateAvailable,
    isHoldAvailable,
    isResumeAvailable
  };
};
